import jsPDF from 'jspdf';
import { RUBICON_LOGO_BASE64 } from './pdfAssets';
import { RISK_QUESTIONS, RISK_BANDS } from './riskQuestions';

/**
 * Generates a printable blank Investor Risk Profile form.
 * Advisors download this to hand-fill offline when an investor
 * is not available to complete the online form.
 */
export const downloadBlankRiskProfileForm = () => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let yPos = 12;

  const addFooter = (pageNum) => {
    const footerY = pageHeight - 22;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(
      '6A Dr.Rajendra Prasad Sarani, 4th Floor, R.No-20, Kolkata-700001',
      pageWidth / 2,
      footerY,
      { align: 'center' }
    );
    doc.text(
      'Email: communications@rubiconfinservices.com | Website: www.rubiconfinservices.com | Tel: +91-33-40442577',
      pageWidth / 2,
      footerY + 4,
      { align: 'center' }
    );
    doc.setFont('helvetica', 'normal');
    doc.text(`Page ${pageNum}`, pageWidth - 20, pageHeight - 8, { align: 'right' });
    doc.setTextColor(0, 0, 0);
  };

  // Logo
  try {
    const logoWidth = 40;
    const logoHeight = 38;
    doc.addImage(
      RUBICON_LOGO_BASE64,
      'JPEG',
      (pageWidth - logoWidth) / 2,
      yPos,
      logoWidth,
      logoHeight
    );
    yPos += 42;
  } catch (e) {
    yPos += 20;
  }

  // Company + registration
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(30, 58, 138);
  doc.text('RUBICON FINSERVICES', pageWidth / 2, yPos, { align: 'center' });
  yPos += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text('AMFI Registered Mutual Fund Distributor', pageWidth / 2, yPos, { align: 'center' });
  yPos += 4;
  doc.text(
    'ARN: 102867 | Initial Registration: 09-June-2015 | Valid till: 01-07-2027',
    pageWidth / 2,
    yPos,
    { align: 'center' }
  );
  yPos += 8;

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.text('INVESTOR RISK PROFILE — BLANK FORM', pageWidth / 2, yPos, { align: 'center' });
  yPos += 4;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(80, 80, 80);
  doc.text(
    '(For offline hand-filling by the investor. Advisor: tick one option per question.)',
    pageWidth / 2,
    yPos,
    { align: 'center' }
  );
  yPos += 7;

  // Investor Information box
  doc.setDrawColor(59, 130, 246);
  doc.setLineWidth(0.4);
  doc.setFillColor(240, 249, 255);
  doc.rect(15, yPos, pageWidth - 30, 32, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text('Investor Information', 18, yPos + 6);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const line = (label, x, y, w) => {
    doc.text(label, x, y);
    const startX = x + doc.getTextWidth(label) + 2;
    doc.setDrawColor(120, 120, 120);
    doc.line(startX, y + 1, x + w, y + 1);
  };
  line('Full Name:', 18, yPos + 14, 90);
  line('Age:', 115, yPos + 14, 70);
  line('Email:', 18, yPos + 22, 90);
  line('Phone:', 115, yPos + 22, 70);
  line('Date:', 18, yPos + 30, 60);
  line('Place:', 85, yPos + 30, 100);
  yPos += 38;

  // Questions header
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(30, 58, 138);
  doc.text('Risk Assessment Questionnaire', 15, yPos);
  yPos += 2;
  doc.setDrawColor(30, 58, 138);
  doc.setLineWidth(0.4);
  doc.line(15, yPos, pageWidth - 15, yPos);
  yPos += 5;

  // Questions
  RISK_QUESTIONS.forEach((q, idx) => {
    // IMPORTANT: set the rendering font BEFORE measuring/splitting text.
    // jsPDF's splitTextToSize uses the CURRENT font state to compute widths,
    // so if the previous section left italic/normal font active, wraps will be off.
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);

    // Estimate block height (now measured with the actual render font)
    const questionLines = doc.splitTextToSize(`${idx + 1}. ${q.question}`, pageWidth - 30);
    const blockHeight = questionLines.length * 4.5 + q.options.length * 5.5 + 4;

    if (yPos + blockHeight > pageHeight - 32) {
      addFooter(doc.internal.pages.length);
      doc.addPage();
      yPos = 15;
    }

    doc.setTextColor(0, 0, 0);
    doc.text(questionLines, 15, yPos);
    yPos += questionLines.length * 4.5 + 1;

    q.options.forEach((opt) => {
      // Set the option font BEFORE measuring/splitting.
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);

      // Checkbox
      doc.setDrawColor(80, 80, 80);
      doc.setLineWidth(0.3);
      doc.rect(20, yPos - 3, 3.5, 3.5);

      // Option label (wrapped) + points badge on the right.
      // Points badge is rendered in ITALIC 9 — measure it in that font.
      doc.setFont('helvetica', 'italic');
      const pointsLabel = opt.points === 1 ? '1 pt' : `${opt.points} pts`;
      const pointsWidth = doc.getTextWidth(pointsLabel) + 4;

      // Switch BACK to normal for label measurement + render.
      doc.setFont('helvetica', 'normal');
      const optMaxWidth = pageWidth - 40 - pointsWidth;
      const optLines = doc.splitTextToSize(opt.label, optMaxWidth);
      doc.setTextColor(0, 0, 0);
      doc.text(optLines, 26, yPos);

      // Points badge (right-aligned, muted italic)
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(120, 120, 120);
      doc.text(pointsLabel, pageWidth - 18, yPos, { align: 'right' });

      // Reset font state so the NEXT iteration / question measures correctly.
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);

      yPos += Math.max(5, optLines.length * 4.2);
    });
    yPos += 2;
  });

  // Declaration & signature
  if (yPos + 55 > pageHeight - 32) {
    addFooter(doc.internal.pages.length);
    doc.addPage();
    yPos = 15;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(30, 58, 138);
  doc.text('Investor Declaration & Signature', 15, yPos);
  yPos += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  const declaration =
    'I confirm that the answers provided above accurately reflect my current financial situation and risk appetite. I understand that this profile will be used solely for the purpose of providing suitable investment recommendations. I have read and understood the disclaimer overleaf.';
  const declLines = doc.splitTextToSize(declaration, pageWidth - 30);
  doc.text(declLines, 15, yPos);
  yPos += declLines.length * 4.5 + 6;

  // Signature box on the right; Place / Date lines on the left
  line('Place:', 15, yPos, 80);
  line('Date:', 15, yPos + 10, 80);

  const sigBoxX = pageWidth - 85;
  const sigBoxY = yPos - 6;
  const sigBoxW = 70;
  const sigBoxH = 20;
  doc.setDrawColor(120, 120, 120);
  doc.rect(sigBoxX, sigBoxY, sigBoxW, sigBoxH);
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text('Investor Signature', sigBoxX + sigBoxW / 2, sigBoxY + sigBoxH + 4, { align: 'center' });

  // Advisor use box
  yPos += 22;
  doc.setDrawColor(200, 200, 200);
  doc.setFillColor(250, 250, 250);
  doc.rect(15, yPos, pageWidth - 30, 22, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text('For Advisor Use Only', 18, yPos + 5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  line('Advisor Name:', 18, yPos + 12, 80);
  line('ARN:', 105, yPos + 12, 60);
  line('Advisor Signature:', 18, yPos + 19, 80);
  line('Date:', 105, yPos + 19, 60);

  // ─── How Scoring Works / Risk Profile Bands (new page) ───
  doc.addPage();
  yPos = 15;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(30, 58, 138);
  doc.text('How the Risk Profile is Calculated', pageWidth / 2, yPos, { align: 'center' });
  yPos += 8;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(0, 0, 0);
  const intro = doc.splitTextToSize(
    'Each option in the questionnaire above carries a point value (shown in italics on the right of each option). ' +
      'The investor selects exactly one option per question. To determine the investor\'s Risk Profile:',
    pageWidth - 30
  );
  doc.text(intro, 15, yPos);
  yPos += intro.length * 4.8 + 3;

  const steps = [
    'Step 1 — Add up the point values of every selected option across all 10 questions.',
    'Step 2 — Compare the total against the Risk Profile bands below.',
    'Step 3 — Enter the total score and the resulting Risk Profile in the Advisor Use Only box on Page 1.',
  ];
  doc.setFont('helvetica', 'bold');
  steps.forEach((s) => {
    const sLines = doc.splitTextToSize(s, pageWidth - 30);
    doc.text(sLines, 15, yPos);
    yPos += sLines.length * 4.8 + 1;
  });
  yPos += 4;

  // Table: Risk Profile Bands
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(30, 58, 138);
  doc.text('Risk Profile Bands', 15, yPos);
  yPos += 5;

  const bandColW = [40, 30, pageWidth - 30 - 70];
  const bandRowH = 16;

  // Header row
  doc.setFillColor(30, 58, 138);
  doc.rect(15, yPos, pageWidth - 30, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.text('Risk Profile', 18, yPos + 5.5);
  doc.text('Score Range', 15 + bandColW[0] + 3, yPos + 5.5);
  doc.text('Description', 15 + bandColW[0] + bandColW[1] + 3, yPos + 5.5);
  yPos += 8;

  const bandColors = {
    Conservative: [219, 234, 254],
    Moderate: [254, 243, 199],
    Aggressive: [254, 226, 226],
  };

  RISK_BANDS.forEach((band) => {
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.setFillColor(...(bandColors[band.label] || [245, 245, 245]));
    doc.rect(15, yPos, pageWidth - 30, bandRowH, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(band.label, 18, yPos + 6);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const range = band.max >= 100 ? `${band.min}+` : `${band.min} – ${band.max}`;
    doc.text(range, 15 + bandColW[0] + 3, yPos + 6);

    const descLines = doc.splitTextToSize(band.description, bandColW[2] - 4);
    doc.text(descLines, 15 + bandColW[0] + bandColW[1] + 3, yPos + 6);

    yPos += bandRowH;
  });
  yPos += 4;

  // Example / Tip
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  const example =
    'Example: An investor answering all "most-aggressive" options would score close to 100 (Aggressive band). ' +
    'A "safest-choice" investor would score around 20 (Conservative band).';
  const exLines = doc.splitTextToSize(example, pageWidth - 30);
  doc.text(exLines, 15, yPos);
  yPos += exLines.length * 4.5 + 5;

  // Note about ties/boundary
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(180, 0, 0);
  doc.setFontSize(8.5);
  const note = doc.splitTextToSize(
    'Note: If the investor selects multiple options in the same question, or leaves a question blank, ' +
      'the form is INVALID for scoring — please have the investor re-attempt that question.',
    pageWidth - 30
  );
  doc.text(note, 15, yPos);
  yPos += note.length * 4.5 + 6;

  // Summary calculation grid — advisors can tally scores here
  doc.setDrawColor(59, 130, 246);
  doc.setLineWidth(0.4);
  doc.setFillColor(240, 249, 255);
  doc.rect(15, yPos, pageWidth - 30, 60, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(30, 58, 138);
  doc.text('Advisor Scoring Sheet', 18, yPos + 6);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);

  // Column headers
  const gridStartY = yPos + 11;
  const colWidth = (pageWidth - 40) / 10;
  for (let i = 1; i <= 10; i++) {
    doc.text(`Q${i}`, 20 + (i - 1) * colWidth, gridStartY, { align: 'left' });
  }

  // Points row
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('Points:', 20, gridStartY + 6);
  doc.setFont('helvetica', 'normal');
  for (let i = 1; i <= 10; i++) {
    const bx = 20 + (i - 1) * colWidth;
    doc.setDrawColor(120, 120, 120);
    doc.rect(bx, gridStartY + 8, colWidth - 3, 8);
  }

  // Total
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Total Score:', 18, gridStartY + 26);
  doc.setDrawColor(30, 58, 138);
  doc.setLineWidth(0.6);
  doc.rect(50, gridStartY + 21, 40, 8);

  doc.text('Risk Profile:', 100, gridStartY + 26);
  doc.rect(135, gridStartY + 21, 60, 8);

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text(
    '(Tick applicable band: [ ] Conservative   [ ] Moderate   [ ] Aggressive)',
    18,
    gridStartY + 38
  );

  // Footers
  const total = doc.internal.pages.length - 1;
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    addFooter(i);
  }

  doc.save(
    `Rubicon_Investor_Risk_Profile_BlankForm_${new Date().toISOString().split('T')[0]}.pdf`
  );
};
